const COMPRESSIBLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MIN_SIZE_TO_COMPRESS = 300 * 1024;
const MAX_DIMENSION = 1920;
const OUTPUT_TYPE = 'image/webp';
const OUTPUT_QUALITY = 0.8;

export class ImageCompressUtils {
  /**
   * Downscale and re-encode an image so the base64 kept in the editor content
   * stays small. GIF/SVG, small images, and results that are not smaller than
   * the original are returned untouched.
   *
   * @param blob image picked or pasted by the user
   * @returns data URI of the (possibly compressed) image
   */
  public static async toCompressedDataUri(blob: Blob): Promise<string> {
    const compressed = await ImageCompressUtils.compress(blob);

    return ImageCompressUtils.readAsDataUri(compressed ?? blob);
  }

  private static async compress(blob: Blob): Promise<Blob | null> {
    if (
      !COMPRESSIBLE_TYPES.has(blob.type) ||
      blob.size <= MIN_SIZE_TO_COMPRESS
    ) {
      return null;
    }

    let bitmap: ImageBitmap;

    try {
      bitmap = await createImageBitmap(blob);
    } catch {
      return null;
    }

    const scale = Math.min(
      1,
      MAX_DIMENSION / Math.max(bitmap.width, bitmap.height)
    );
    const canvas = document.createElement('canvas');

    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext('2d');

    // A missing context would encode a blank canvas, replacing the user's image.
    context?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    if (!context) {
      return null;
    }

    const compressed = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, OUTPUT_TYPE, OUTPUT_QUALITY)
    );

    return compressed && compressed.size < blob.size ? compressed : null;
  }

  private static readAsDataUri(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.addEventListener('load', () => resolve(reader.result as string));
      reader.addEventListener('error', () => reject(reader.error));
      reader.readAsDataURL(blob);
    });
  }
}
